import { inject, injectable } from '@servicetitan/react-ioc';
import {
    SavedCreditCardsApiService,
    CreditCard,
    CreditCardIssuer,
    EditCreditCard,
    ChargeApiService,
    PortalUser,
} from 'src/api';
import { computed, observable, action, runInAction } from 'mobx';
import { cloneDeep } from 'src/util/clone-deep';
import { RouterStore } from 'src/core/stores/router.store';
import { lazyInject } from 'src/ioc';
import { ClientDataStore } from 'src/core/stores/client-data.store';
import {
    PaymentGateway,
    PGResponse,
    PaymentGatewayResponseType,
} from 'src/statement/stores/payment-gateway.store';
import { FieldState, FormState } from 'formstate';
import * as Validators from 'src/invoice/components/card-info/cc-validators';
import { MembershipNameTypeStore } from 'src/core/stores/membership-name-type.store';
import { DropdownFieldState } from '@servicetitan/form';
import { AnvilSelectOptionsProps } from '@servicetitan/design-system';

// Interfaces

@injectable()
export class SavedCreditCardsStore {
    constructor(
        private savedCreditCardsApi: SavedCreditCardsApiService,
        private readonly paymentGatewayService: PaymentGateway,
        private readonly chargeApi: ChargeApiService
    ) {
        this.init();
    }

    @lazyInject(RouterStore) router!: RouterStore;
    @lazyInject(ClientDataStore) private clientData!: ClientDataStore;
    @inject(MembershipNameTypeStore) private membershipNameTypeStore!: MembershipNameTypeStore;

    // Variables
    customerIds: number[] | undefined;

    customerInfos: PortalUser[] = [];

    postalCode = new FieldState('').validators(Validators.zipPostalCodeValidator);

    form = new FormState({ postalCode: this.postalCode });

    // Observable variables
    @observable
    savedCreditCards: CreditCard[] | undefined;

    @observable
    isLoadingSavedCreditCards = true;

    @observable
    isChangeCardDialogOpen = false;

    @observable
    isAddNewCardDialogOpen = false;

    @observable
    isDeleteCardDialogOpen = false;

    @observable
    isSetPrimaryModalOpen = false;

    @observable
    isDeleteModalOpen = false;

    @observable
    isAddNewCardModalOpen = false;

    @observable
    currentEditingCard: CreditCard | undefined;

    @observable
    selectedCardToPay: CreditCard | undefined;

    @observable
    selectedCardToUpdate: CreditCard | undefined;

    @observable errorMessage?: string;

    @observable cardProcessing: boolean = false;

    @observable
    isMobileCardSelectTakeoverOpen = false;

    @observable
    isMobileCardDeleteTakeoverOpen = false;

    @observable
    isMobileCardAddTakeoverOpen = false;

    @observable
    isMobileSetPrimaryTakeoverOpen = false;

    @observable
    needToStoreAsPrimary = false;

    @observable
    isSavedCreditCardEnabled = false;

    @observable
    isSavedCreditCardFeatureOn = false;


    customerSelector = new FieldState<number | undefined>(undefined).onDidChange(({ newValue }) =>
        this.getSavedCreditCardsForCustomer(newValue));

    @observable
    customerOptions: AnvilSelectOptionsProps = [];

    // Methods
    init = () => {
        this.savedCreditCardsApi.feature().then((response) => {
            this.isSavedCreditCardFeatureOn = response.data;
            if (this.isSavedCreditCardFeatureOn && this.clientData.isLoggedIn) {
                this.getCustomerIds();
            }
        });
        this.chargeApi.paymentGatewayConfig().then((response) => {
            this.paymentGatewayService.apiKey = response.data.apiKey || '';
            this.paymentGatewayService.baseUrl = response.data.baseUrl || '';
            this.paymentGatewayService.paymentsServiceJwt = response.data.paymentsServiceJwt || '';
            this.paymentGatewayService.paymentsServiceCreditCardProcessorExists = response.data.paymentsServiceCreditCardProcessorExists || false;
        });
    }

    getCardIssuer = (issuer: CreditCardIssuer) => {
        switch (issuer) {
            case CreditCardIssuer.NUMBER_3:
                return 'AMEX';
            case CreditCardIssuer.NUMBER_4:
                return 'VISA';
            case CreditCardIssuer.NUMBER_5:
                return 'MC';
            case CreditCardIssuer.NUMBER_6:
                return 'Discover';
            default:
                return 'Card';
        }
    }

    isExpiringSoon = (year: number, month: number) => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;
        if (currentYear === year) {
            if (currentMonth === month || currentMonth + 1 === month) {
                return true;
            }
        } else if (currentYear + 1 === year && currentMonth === 12 && month === 1) {
            return true;
        }
        return false;
    }

    convertToCardIssuer = (name: string | undefined): CreditCardIssuer => {
        if (name === undefined) {
            return CreditCardIssuer.NUMBER_0;
        }
        switch (name.toLowerCase()) {
            case 'americanexpress':
                return CreditCardIssuer.NUMBER_3;
            case 'visa':
                return CreditCardIssuer.NUMBER_4;
            case 'mastercard':
                return CreditCardIssuer.NUMBER_5;
            case 'discover':
                return CreditCardIssuer.NUMBER_6;
            default:
                return CreditCardIssuer.NUMBER_0;
        }
    }

    sortCreditCard = (cards: CreditCard[]) => {
        if (cards.length === 0) {
            return [];
        }
        const primaryCards = cards.filter(c => c.primary && !c.isExpired);
        const membershipCards = cards.filter(c => !c.primary && c.membership && !c.isExpired);
        const others = cards
            .filter(c => c.isExpired || (!c.primary && !c.membership))
            .sort((a, b) => b.expYear + b.expMonth - (a.expYear + a.expMonth));
        return [...primaryCards, ...membershipCards, ...others];
    }

    gotoSavedCardPage = () => {
        this.router.history.push('/savedpaymentmethod/paymentmethod');
    }

    isSelectedToUpdate = (card: CreditCard) => {
        if (this.selectedCardToUpdate) {
            return card.id === this.selectedCardToUpdate.id;
        }
        return undefined;
    }

    usePaymentGateway = () => {
        return this.paymentGatewayService && !!this.paymentGatewayService.baseUrl;
    }

    getCustomerId = () => {
        return this.customerIds && this.customerIds.length > 0 ?
            this.customerIds[0] : 0;
    }

    // Action Methods

    @action
    getSavedCreditCardsForCustomer = async (id?: number) => {
        this.isLoadingSavedCreditCards = true;
        if (!id) {
            this.isLoadingSavedCreditCards = false;
            return;
        }
        const allCards = (await this.savedCreditCardsApi.savedCreditCards(id)).data;
        const availableCards = allCards.filter(c => !c.isExpired);

        runInAction(() => {
            this.savedCreditCards = this.sortCreditCard(allCards);
            this.isLoadingSavedCreditCards = false;
            this.selectedCardToPay =
                availableCards.length > 0
                    ? availableCards.find(c => c.primary) || availableCards[0]
                    : undefined;
        });
    }

    @action
    openSetPrimaryModal = (card: CreditCard) => {
        this.currentEditingCard = cloneDeep(card);
        this.isSetPrimaryModalOpen = true;
    }

    @action
    closeSetPrimaryModal = () => {
        this.currentEditingCard = undefined;
        this.isSetPrimaryModalOpen = false;
    }

    @action
    openDeleteModal = (card: CreditCard) => {
        this.resetErrorMessage();
        this.currentEditingCard = cloneDeep(card);
        this.isDeleteModalOpen = true;
    }

    @action
    closeDeleteModal = () => {
        this.currentEditingCard = undefined;
        this.isDeleteModalOpen = false;
        this.selectedCardToUpdate = undefined;
        this.form.$.postalCode.onChange('');
        this.form.$.postalCode.dirty = false;
    }

    @action
    openAddNewCardModal = () => {
        this.resetErrorMessage();
        this.isAddNewCardModalOpen = true;
        this.needToStoreAsPrimary = false;
    }

    @action
    closeAddNewCardModal = () => {
        this.isAddNewCardModalOpen = false;
        this.form.$.postalCode.onChange('');
        this.form.$.postalCode.dirty = false;
    }

    @action
    openMobileCardSelectTakeover = () => {
        this.resetErrorMessage();
        this.isMobileCardSelectTakeoverOpen = true;
    }

    @action
    closeMobileCardSelectTakeover = () => {
        this.isMobileCardSelectTakeoverOpen = false;
    }

    @action
    openMobileCardDeleteTakeover = (card: CreditCard) => {
        this.resetErrorMessage();
        this.currentEditingCard = cloneDeep(card);
        this.isMobileCardDeleteTakeoverOpen = true;
    }

    @action
    closeMobileCardDeleteTakeover = () => {
        this.isMobileCardDeleteTakeoverOpen = false;
    }

    @action
    openMobileSetPrimaryTakeover = (card: CreditCard) => {
        this.currentEditingCard = cloneDeep(card);
        this.isMobileSetPrimaryTakeoverOpen = true;
    }

    @action
    closeMobileSetPrimaryTakeover = () => {
        this.isMobileSetPrimaryTakeoverOpen = false;
    }

    @action
    openMobileCardAddTakeover = () => {
        this.resetErrorMessage();
        this.needToStoreAsPrimary = false;
        this.isMobileCardAddTakeoverOpen = true;
    }

    @action
    closeMobileCardAddTakeover = () => {
        this.isMobileCardAddTakeoverOpen = false;
        this.form.$.postalCode.onChange('');
        this.form.$.postalCode.dirty = false;
    }

    @action
    getCustomerIds = async () => {
        const response = await this.savedCreditCardsApi.isEnabled();
        runInAction(() => {
            this.isSavedCreditCardEnabled = !!response.data.length;
            this.customerIds = response.data.map(i => i.id);
            this.customerInfos = response.data.map(i => ({ id: i.id, name: i.name }));
            this.customerOptions = this.customerInfos.map(i => ({ value: i.id, text: i.name }));
            if (this.customerIds.length) {
                this.customerSelector.onChange(this.customerIds[0]);
            }
        });
    }

    /// If user choose to use a new card instead of the default primary card, reset the value to undefined.
    @action
    setSelectedCardToPay = (selectedCard: CreditCard | undefined) => {
        this.selectedCardToPay = selectedCard;
    }

    @action
    setSelectedCardToUpdate = (selectedCard: CreditCard | undefined) => {
        this.selectedCardToUpdate = selectedCard;
    }

    @action
    setDefaultSelectedCardToPay = () => {
        if (!this.savedCreditCards || this.savedCreditCards.length === 0) {
            return undefined;
        }
        const availableCards = this.savedCreditCards.filter(c => !c.isExpired);
        this.selectedCardToPay =
            availableCards.length > 0
                ? availableCards.find(c => c.primary) || availableCards[0]
                : undefined;
    }

    @action
    setPrimary = (value: boolean) => (this.needToStoreAsPrimary = value)

    @action
    setAsPrimary = async (card: CreditCard) => {
        await this.savedCreditCardsApi.assignCreditCard(
            card.customerId,
            card.id,
            true,
            card.membership
        );
        runInAction(() => {
            this.currentEditingCard = undefined;
            this.isSetPrimaryModalOpen = false;
            this.isMobileSetPrimaryTakeoverOpen = false;
        });
        await this.getSavedCreditCardsForCustomer(this.customerSelector.value);
    }

    @action
    handleDelete = async () => {
        this.cardProcessing = true;
        this.resetErrorMessage();
        try {
            await this.savedCreditCardsApi.deleteCreditCard(
                this.currentEditingCard!.customerId,
                this.currentEditingCard!.id
            );
        } catch (e) {
            runInAction(() => {
                this.errorMessage = e;
            });
        } finally {
            runInAction(() => {
                this.cardProcessing = false;
                this.closeDeleteModal();
            });
            await this.getSavedCreditCardsForCustomer(this.customerSelector.value);
        }
    }

    @action
    handleAdd = async () => {
        this.resetErrorMessage();
        this.cardProcessing = true;
        await this.form.validate();
        this.paymentGatewayService.handleSubmit(this.processAddNewCard);
    }

    @action
    processAddNewCard = async (pgResponse: PGResponse) => {
        if (pgResponse.Type === PaymentGatewayResponseType.Error || this.form.hasError) {
            this.cardProcessing = false;
            return;
        }
        const responseData = pgResponse.Data;
        const selectedCustomerId = this.customerSelector.value!;
        try {
            const cardInfo: EditCreditCard = {
                id: 0,
                customerId: selectedCustomerId,
                primary: this.needToStoreAsPrimary,
                membership: true,
                businessUnitId: 0,
                isExpired: false,
                issuer: this.convertToCardIssuer(responseData.IssuerName),
                expYear: parseInt(responseData.ExpYear, 10),
                expMonth: parseInt(responseData.ExpMonth, 10),
                cvv: responseData.Cvc,
                number: responseData.Number,
                cardholderName: responseData.CardholderName,
                zip: this.postalCode.$.trim(),
                token: responseData.Token,
            };

            await this.savedCreditCardsApi.saveCreditCard(selectedCustomerId, cardInfo);
            await this.getSavedCreditCardsForCustomer(selectedCustomerId);
        } catch (e) {
            runInAction(() => {
                this.errorMessage = e;
            });
        } finally {
            runInAction(() => {
                this.cardProcessing = false;
                if (this.isAddNewCardModalOpen) {
                    this.closeAddNewCardModal();
                }
                if (this.isMobileCardAddTakeoverOpen) {
                    this.closeMobileCardAddTakeover();
                }
            });
        }
    }

    @action
    resetErrorMessage() {
        this.errorMessage = undefined
    }

    // Computed
    @computed get phone() {
        return this.clientData.portalData ? this.clientData.portalData.brandData.phone : undefined;
    }

    @computed get membershipName() {
        return this.membershipNameTypeStore.name || 'Membership';
    }

    @computed get availableCardsCount() {
        return this.savedCreditCards?.filter(c => !c.isExpired).length || 0;
    }
}
